import { useRef } from 'react';
import clsx from 'clsx';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../../common/Catalyst/button';
import { ACCEPT_ATTRIBUTE, formatFileSize } from './attachmentRules';
import { StagedFile } from './useAttachmentUploads';

interface Props {
  staged: StagedFile[];
  disabled?: boolean;
  label?: string;
  /** A bare paperclip, for a composer's toolbar; the label becomes its
   *  accessible name and tooltip. */
  compact?: boolean;
  onStage: (files: FileList) => void;
  onRemove: (key: string) => void;
}

const statusTone = (status: StagedFile['status']): string => {
  switch (status) {
    case 'rejected':
    case 'failed':
      return 'ring-red-300 bg-red-50 dark:bg-red-950/30 dark:ring-red-800';
    case 'done':
      return 'ring-green-300 bg-green-50 dark:bg-green-950/30 dark:ring-green-800';
    default:
      return 'ring-zinc-200 dark:ring-zinc-700';
  }
};

/**
 * Picking files, and the chip row that reports what happened to each.
 *
 * A plain file input rather than a drop zone. Nothing in this app has a
 * drop zone, and a second interaction surface brings a drag-state bug
 * class and an accessibility obligation for a control used a handful of
 * times per violation. One can be added later behind the same handler.
 */
export function AttachmentPicker({
  staged,
  disabled = false,
  label = 'Attach files',
  compact = false,
  onStage,
  onRemove,
}: Props) {
  const input = useRef<HTMLInputElement | null>(null);

  return (
    <div>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        aria-label={label}
        data-testid="attachment-file-input"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onStage(e.target.files);
          // Cleared so picking the same file twice in a row still fires.
          e.target.value = '';
        }}
      />

      {compact ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => input.current?.click()}
          aria-label={label}
          title={label}
          className="rounded-md p-1.5 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-40"
        >
          <PaperClipIcon className="h-4 w-4" />
        </button>
      ) : (
        <Button
          outline
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          <PaperClipIcon className="h-4 w-4" />
          {label}
        </Button>
      )}

      {staged.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {staged.map((item) => (
            <li
              key={item.key}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ring-1',
                statusTone(item.status)
              )}
            >
              <span className="max-w-[12rem] truncate">{item.file.name}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {item.status === 'uploading'
                  ? 'uploading…'
                  : item.status === 'done'
                    ? 'attached'
                    : (item.message ?? formatFileSize(item.file.size))}
              </span>
              {item.status !== 'uploading' && item.status !== 'done' && (
                <button
                  type="button"
                  aria-label={`Remove ${item.file.name}`}
                  onClick={() => onRemove(item.key)}
                  className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AttachmentPicker;
